import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Globe, Info, Lock, Play, Terminal, Upload, Wand2 } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import { Tool, useAddTool, useUpdateTool } from "@/lib/tools-store";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

const toolSchema = z.object({
  name: z.string().min(3, "Name is required"),
  description: z.string().min(5, "Description is required"),
  category: z.string(),
  type: z.string(),
  endpoint: z.string().url("Invalid URL").optional().or(z.literal("")),
});

type ToolFormValues = z.infer<typeof toolSchema>;

interface ToolModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editTool?: Tool;
}

const ANALYTICS_DATA = [
  { name: "Jan", usage: 4000, latency: 420 },
  { name: "Feb", usage: 7200, latency: 450 },
  { name: "Mar", usage: 12000, latency: 410 },
  { name: "Apr", usage: 15402, latency: 380 },
];

const DEFAULT_SCHEMA = `{
  "type": "object",
  "properties": {
    "query": { "type": "string" },
    "limit": { "type": "number", "default": 10 }
  },
  "required": ["query"]
}`;

export function ToolModal({ open, onOpenChange, editTool }: ToolModalProps) {
  const { t } = useTranslation();
  const addTool = useAddTool();
  const updateTool = useUpdateTool();

  const [isLoading, setIsLoading] = useState(false);
  const [schemaDraft, setSchemaDraft] = useState(DEFAULT_SCHEMA);
  const [playgroundQuery, setPlaygroundQuery] = useState("");
  const [playgroundLimit, setPlaygroundLimit] = useState("10");

  const form = useForm<ToolFormValues>({
    resolver: zodResolver(toolSchema),
    defaultValues: {
      name: editTool?.name || "",
      description: editTool?.description || "",
      category: editTool?.category || "Productivity",
      type: editTool?.type || "API",
      endpoint: "",
    },
  });

  const previewResponse = useMemo(() => {
    const endpoint = form.watch("endpoint") || "https://api.service.com/v1";
    const query = playgroundQuery.trim() || "[query]";
    const limit = Number(playgroundLimit || "10");

    return JSON.stringify(
      {
        request: {
          endpoint,
          payload: {
            query,
            limit: Number.isNaN(limit) ? 10 : limit,
          },
        },
        response: {
          status: 200,
          source: "preview-local",
          summary: "Phản hồi xem trước được dựng từ dữ liệu biểu mẫu hiện tại.",
        },
      },
      null,
      2,
    );
  }, [form, playgroundLimit, playgroundQuery]);

  const handleLintSchema = () => {
    try {
      JSON.parse(schemaDraft);
      toast.success("JSON schema hợp lệ.");
    } catch {
      toast.error("JSON schema không hợp lệ.");
    }
  };

  const handleGenerateSchema = () => {
    const description = form.getValues("description").trim() || "Custom tool";
    const name = form.getValues("name").trim() || "custom_tool";
    const suggestedSchema = {
      type: "object",
      properties: {
        query: { type: "string", description },
        source: { type: "string", default: name.toLowerCase().replace(/\s+/g, "_") },
        limit: { type: "number", default: 10 },
      },
      required: ["query"],
    };

    setSchemaDraft(JSON.stringify(suggestedSchema, null, 2));
    toast.success("Đã tạo gợi ý schema từ thông tin hiện tại.");
  };

  const handlePreviewRequest = () => {
    toast.success("Đã cập nhật bản xem trước request.");
  };

  const onSubmit = async (data: ToolFormValues) => {
    setIsLoading(true);
    try {
      const nextTool: Tool = {
        id: editTool?.id || `tool-${crypto.randomUUID()}`,
        name: data.name,
        slug: data.name.toLowerCase().replace(/\s+/g, "-"),
        category: data.category as Tool["category"],
        description: data.description,
        type: data.type as Tool["type"],
        provider: "Custom",
        status: "connected",
        iconUrl: editTool?.iconUrl || "https://cdn-icons-png.flaticon.com/512/10434/10434151.png",
        tags: editTool?.tags || ["custom"],
        isFavorite: editTool?.isFavorite || false,
        price: editTool?.price || 0,
        metrics: editTool?.metrics || { calls: 0, avgCost: 0, successRate: 100, latency: 0 },
      };

      if (editTool) {
        updateTool(editTool.id, nextTool);
      } else {
        addTool(nextTool);
      }

      toast.success(editTool ? "Đã cập nhật tool." : "Đã tạo tool mới.");
      onOpenChange(false);
    } catch {
      toast.error("Không thể lưu tool.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-w-4xl flex-col overflow-hidden border-white/5 bg-card p-0 shadow-2xl" aria-describedby="tool-modal-description">
        <DialogHeader className="border-b border-white/5 bg-muted/30 p-6">
          <div className="flex items-center justify-between">
            <DialogTitle id="tool-modal-title">{editTool ? `Chỉnh sửa: ${editTool.name}` : t("create_tool")}</DialogTitle>
            <Button variant="outline" size="sm" className="h-8 gap-2 text-xs" type="button" onClick={handleGenerateSchema} aria-label="Tạo gợi ý schema">
              <Wand2 className="h-3.5 w-3.5" /> Gợi ý schema
            </Button>
          </div>
          <p id="tool-modal-description" className="sr-only">
            Modal dùng để tạo hoặc chỉnh sửa tool và cấu hình kỹ thuật tương ứng.
          </p>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
          <Tabs defaultValue="basic" className="flex min-h-0 flex-1 flex-col">
            <TabsList className="h-12 w-full justify-start rounded-none border-b border-white/5 bg-transparent px-6">
              <TabsTrigger value="basic">{t("tab_basic")}</TabsTrigger>
              <TabsTrigger value="tech">{t("tab_tech")}</TabsTrigger>
              <TabsTrigger value="playground">{t("playground")}</TabsTrigger>
              <TabsTrigger value="permissions">{t("tab_permissions")}</TabsTrigger>
              <TabsTrigger value="analytics">{t("tab_analytics")}</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-y-auto p-6">
              <TabsContent value="basic" className="m-0 space-y-6">
                <div className="flex gap-6">
                  <div
                    className="flex h-24 w-24 shrink-0 cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-white/10 bg-secondary transition-colors hover:border-primary"
                    role="button"
                    aria-label="Biểu tượng tool"
                    onClick={() => toast.info("Bạn có thể nối tính năng tải icon riêng khi backend media đã sẵn sàng.")}
                  >
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <span className="mt-2 text-[10px] text-muted-foreground">128x128</span>
                  </div>

                  <div className="flex-1 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="tool-name">Tên tool</Label>
                        <Input id="tool-name" placeholder="Salesforce Sync" {...form.register("name")} />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="tool-category">Nhóm</Label>
                        <select
                          id="tool-category"
                          className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                          {...form.register("category")}
                        >
                          <option value="CRM">CRM</option>
                          <option value="Finance">Finance</option>
                          <option value="Communication">Communication</option>
                          <option value="Productivity">Productivity</option>
                          <option value="Development">Development</option>
                          <option value="Security">Security</option>
                          <option value="Sales">Sales</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="tool-description">Mô tả</Label>
                      <Textarea id="tool-description" placeholder="Mô tả rõ tool này hỗ trợ agent thực hiện nghiệp vụ gì..." {...form.register("description")} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tech" className="m-0 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tool-type">Loại tool</Label>
                    <select
                      id="tool-type"
                      className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                      {...form.register("type")}
                    >
                      <option value="API">REST API</option>
                      <option value="Function">Local Function</option>
                      <option value="DB">Database Query</option>
                      <option value="RAG">RAG Pipeline</option>
                      <option value="Custom">Custom</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tool-endpoint">{t("endpoint")}</Label>
                    <Input id="tool-endpoint" placeholder="https://api.service.com/v1" {...form.register("endpoint")} />
                    <p className="text-[10px] italic text-muted-foreground">Endpoint nên là HTTPS để bảo đảm an toàn cho môi trường production.</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="json-schema">{t("json_schema")}</Label>
                    <Button variant="ghost" size="sm" className="h-6 gap-1 text-[10px]" type="button" onClick={handleLintSchema} aria-label="Kiểm tra JSON schema">
                      <Terminal className="h-3 w-3" /> Lint
                    </Button>
                  </div>
                  <Textarea id="json-schema" className="min-h-[200px] font-mono text-[10px]" value={schemaDraft} onChange={(event) => setSchemaDraft(event.target.value)} />
                </div>
              </TabsContent>

              <TabsContent value="playground" className="m-0 space-y-6">
                <div className="space-y-4 rounded-lg border bg-muted/20 p-6">
                  <h4 className="text-sm font-bold">Request Preview</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">query</Label>
                      <Input className="h-9" placeholder="Nhập giá trị thử nghiệm..." aria-label="Query parameter" value={playgroundQuery} onChange={(event) => setPlaygroundQuery(event.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">limit</Label>
                      <Input className="h-9" type="number" aria-label="Limit parameter" value={playgroundLimit} onChange={(event) => setPlaygroundLimit(event.target.value)} />
                    </div>
                  </div>
                  <Button className="w-full gap-2" type="button" onClick={handlePreviewRequest} aria-label="Chạy bản xem trước request">
                    <Play className="h-4 w-4" /> Chạy bản xem trước
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">Response Preview</Label>
                  <div className="min-h-[140px] rounded-lg bg-slate-950 p-4 font-mono text-[10px] text-green-400" role="region" aria-label="API response preview">
                    <pre>{previewResponse}</pre>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="permissions" className="m-0 space-y-6">
                <div className="space-y-4">
                  {[
                    { title: t("pii_masking"), desc: "Tự động che dữ liệu nhạy cảm trong log và payload.", icon: Lock },
                    { title: t("ip_whitelist"), desc: "Giới hạn truy cập theo dải IP nội bộ hoặc đối tác đã duyệt.", icon: Globe },
                    { title: "Team Approval", desc: "Yêu cầu quản trị viên duyệt các thao tác có chi phí cao.", icon: Info },
                  ].map((item) => (
                    <div key={item.title} className="flex items-center justify-between rounded-lg border bg-secondary/20 p-4">
                      <div className="flex gap-3">
                        <item.icon className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.desc}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" type="button" onClick={() => toast.info(`Đã ghi nhận cấu hình cho mục ${item.title}.`)}>
                        Configure
                      </Button>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="m-0 space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="rounded-xl border bg-card/50 p-4">
                    <p className="text-xs uppercase text-muted-foreground">Success Rate</p>
                    <p className="text-2xl font-bold text-green-500">99.8%</p>
                  </div>
                  <div className="rounded-xl border bg-card/50 p-4">
                    <p className="text-xs uppercase text-muted-foreground">Avg Latency</p>
                    <p className="text-2xl font-bold">420ms</p>
                  </div>
                  <div className="rounded-xl border bg-card/50 p-4">
                    <p className="text-xs uppercase text-muted-foreground">Peak Usage</p>
                    <p className="text-2xl font-bold">1.2k/hr</p>
                  </div>
                </div>

                <div className="mt-4 h-[250px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={ANALYTICS_DATA}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="name" fontSize={12} />
                      <YAxis fontSize={12} />
                      <Tooltip contentStyle={{ backgroundColor: "hsl(var(--background))" }} />
                      <Area type="monotone" dataKey="usage" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.1} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </TabsContent>
            </div>
          </Tabs>

          <DialogFooter className="mt-auto border-t border-white/5 bg-muted/30 p-6">
            <Button variant="ghost" onClick={() => onOpenChange(false)} type="button">
              {t("cancel")}
            </Button>
            <Button type="submit" className="min-w-[120px] bg-gradient-primary text-primary-foreground hover:opacity-95" disabled={isLoading} aria-label="Lưu thay đổi tool">
              {isLoading ? "Đang lưu..." : t("save_tool")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
